import { Drawer } from "@mui/material";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type DialogConfig = {
  type: string;
  data?: Record<string, unknown>;
  anchor?: "left" | "right" | "top" | "bottom";
  width?: number | string;
  widthUnit?: "px" | "em" | "%";
  /** Callback fired when dialog closes with result data */
  onResult?: (result: unknown) => void;
  /** Prevent closing via backdrop click and ESC key (default: false) */
  disableExternalClose?: boolean;
};

type DialogStackItem = DialogConfig & {
  id: string;
};

export type DialogServiceContextType = {
  /** Push a new drawer onto the stack */
  openDialog: (config: DialogConfig) => void;
  /** Pop the top drawer from the stack, optionally passing result data to onResult callback */
  closeDialog: (result?: unknown) => void;
  /** Close all drawers in the stack */
  closeAll: () => void;
  /** Current stack depth */
  stackDepth: number;
};

export type DialogComponentProps = {
  data?: Record<string, unknown>;
  onClose: (result?: unknown) => void;
};

export type DialogRegistry = {
  [key: string]: React.ComponentType<DialogComponentProps>;
};

export type DialogServiceConfig = {
  /**
   * Enable URL synchronization for the bottom drawer in the stack (default: false).
   *
   * Behavior with stacked drawers:
   * - Only the bottom/first drawer in the stack is synchronized with URL query parameters.
   * - Drawers opened on top of the bottom drawer are not reflected in the URL.
   * - When the bottom drawer is closed and another drawer becomes the new bottom,
   *   the URL is updated to reflect that new bottom drawer.
   */
  syncWithUrl?: boolean;
  /** Default anchor position (default: "right") */
  defaultAnchor?: "left" | "right" | "top" | "bottom";
  /** Default width (default: 400) */
  defaultWidth?: number | string;
  /** Default width unit (default: "px") */
  defaultWidthUnit?: "px" | "em" | "%";
  /** Query parameter name for dialog type (default: "dialog") */
  queryParamName?: string;
  /** Query parameter name for dialog width (default: "dialogWidth") */
  widthParamName?: string;
};

const DialogServiceContext = createContext<
  DialogServiceContextType | undefined
>(undefined);

type DialogServiceProviderProps = PropsWithChildren<{
  dialogComponents: DialogRegistry;
  config?: DialogServiceConfig;
}>;

const generateId = (): string =>
  `dialog-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const DialogServiceProvider = ({
  children,
  dialogComponents,
  config = {},
}: DialogServiceProviderProps): React.JSX.Element => {
  const {
    syncWithUrl = false,
    defaultAnchor = "right",
    defaultWidth = 400,
    defaultWidthUnit = "px",
    queryParamName = "dialog",
    widthParamName = "dialogWidth",
  } = config;

  const [dialogStack, setDialogStack] = useState<DialogStackItem[]>([]);
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
  // Track data keys added to URL for proper cleanup (using ref to avoid circular deps)
  const urlDataKeysRef = useRef<Set<string>>(new Set());
  // Refs for timeout cleanup on unmount
  const closeTimeoutRefs = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );
  // Ref to access current stack in callbacks without dependency
  const dialogStackRef = useRef<DialogStackItem[]>([]);
  dialogStackRef.current = dialogStack;

  // Sync with URL on mount (if enabled) - only for initial/bottom drawer
  useEffect(() => {
    if (!syncWithUrl) return;

    const params = new URLSearchParams(window.location.search);
    const dialogType = params.get(queryParamName);
    const dialogWidth = params.get(widthParamName);

    if (dialogType) {
      const data: Record<string, unknown> = {};
      params.forEach((value, key) => {
        if (key !== queryParamName && key !== widthParamName) {
          data[key] = value;
        }
      });

      // Parse width and unit from combined value (e.g., "600px", "43.75em", or "40%")
      let width: number | string = defaultWidth;
      let widthUnit: "px" | "em" | "%" = defaultWidthUnit;
      if (dialogWidth) {
        const match = dialogWidth.match(/^([\d.]+)(px|em|%)?$/);
        if (match) {
          width = parseFloat(match[1]);
          widthUnit = (match[2] as "px" | "em" | "%") || defaultWidthUnit;
        }
      }

      setDialogStack([
        {
          id: generateId(),
          type: dialogType,
          data: Object.keys(data).length > 0 ? data : undefined,
          anchor: defaultAnchor,
          width: isNaN(width as number) ? defaultWidth : width,
          widthUnit,
        },
      ]);
    }
  }, [
    syncWithUrl,
    queryParamName,
    widthParamName,
    defaultAnchor,
    defaultWidth,
    defaultWidthUnit,
  ]);

  // Update URL when stack changes (if enabled) - only sync bottom drawer
  useEffect(() => {
    if (!syncWithUrl) return;

    const params = new URLSearchParams(window.location.search);
    const bottomDialog = dialogStack[0];

    if (bottomDialog?.type) {
      params.set(queryParamName, bottomDialog.type);

      if (bottomDialog.width !== undefined && bottomDialog.width !== null) {
        // Only append unit for numeric widths; string widths (e.g., "90vw") are used as-is
        if (typeof bottomDialog.width === "number") {
          const unit = bottomDialog.widthUnit || defaultWidthUnit;
          params.set(widthParamName, `${bottomDialog.width}${unit}`);
        } else {
          params.set(widthParamName, String(bottomDialog.width));
        }
      }

      if (bottomDialog.data) {
        const newDataKeys = new Set<string>();
        Object.entries(bottomDialog.data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.set(key, String(value));
            newDataKeys.add(key);
          }
        });
        urlDataKeysRef.current = newDataKeys;
      }
    } else {
      // Remove all dialog params when stack is empty
      params.delete(queryParamName);
      params.delete(widthParamName);
      // Remove all tracked data keys
      urlDataKeysRef.current.forEach((key) => params.delete(key));
      urlDataKeysRef.current = new Set();
    }

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  }, [
    dialogStack,
    syncWithUrl,
    queryParamName,
    widthParamName,
    defaultWidthUnit,
  ]);

  const openDialog = useCallback(
    (dialogConfig: DialogConfig): void => {
      const newItem: DialogStackItem = {
        id: generateId(),
        ...dialogConfig,
        anchor: dialogConfig.anchor || defaultAnchor,
        width: dialogConfig.width ?? defaultWidth,
        widthUnit: dialogConfig.widthUnit || defaultWidthUnit,
      };
      setDialogStack((prev) => [...prev, newItem]);
    },
    [defaultAnchor, defaultWidth, defaultWidthUnit],
  );

  const closeDialog = useCallback((result?: unknown): void => {
    // Use ref to get current stack without stale closure
    const currentStack = dialogStackRef.current;
    if (currentStack.length === 0) return;

    const topDialog = currentStack[currentStack.length - 1];

    // Call onResult callback if provided, but ignore SyntheticEvent objects
    // (which get passed when closeDialog is used directly as an onClick handler)
    const isEventObject =
      result !== undefined &&
      typeof result === "object" &&
      result !== null &&
      ("nativeEvent" in result || "_reactName" in result);

    if (topDialog.onResult && result !== undefined && !isEventObject) {
      topDialog.onResult(result);
    }

    // Prevent closing a dialog that's already closing
    setClosingIds((prev) => {
      if (prev.has(topDialog.id)) return prev;
      return new Set(prev).add(topDialog.id);
    });

    // Remove from stack after animation completes
    const timeoutId = setTimeout(() => {
      setDialogStack((prev) => {
        // Only remove if this dialog is still in the stack
        const index = prev.findIndex((d) => d.id === topDialog.id);
        if (index === -1) return prev;
        return [...prev.slice(0, index), ...prev.slice(index + 1)];
      });
      setClosingIds((prev) => {
        const next = new Set(prev);
        next.delete(topDialog.id);
        return next;
      });
      closeTimeoutRefs.current.delete(timeoutId);
    }, 300);
    closeTimeoutRefs.current.add(timeoutId);
  }, []);

  const closeAll = useCallback((): void => {
    // Use ref to get current stack without stale closure
    const currentStack = dialogStackRef.current;
    if (currentStack.length === 0) return;

    // Mark all as closing
    const allIds = new Set(currentStack.map((d) => d.id));
    setClosingIds(allIds);

    // Clear stack after animation
    const timeoutId = setTimeout(() => {
      setDialogStack([]);
      setClosingIds(new Set());
      closeTimeoutRefs.current.delete(timeoutId);
    }, 300);
    closeTimeoutRefs.current.add(timeoutId);
  }, []);

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    const timeoutRefs = closeTimeoutRefs.current;
    return () => {
      timeoutRefs.forEach((id) => clearTimeout(id));
      timeoutRefs.clear();
    };
  }, []);

  return (
    <DialogServiceContext.Provider
      value={{
        openDialog,
        closeDialog,
        closeAll,
        stackDepth: dialogStack.length,
      }}
    >
      {children}
      {createPortal(
        <>
          {dialogStack.map((dialog, index) => {
            const DialogComponent = dialogComponents[dialog.type];
            const isTop = index === dialogStack.length - 1;
            const isClosing = closingIds.has(dialog.id);
            const stackOffset = (dialogStack.length - 1 - index) * 40;
            const anchor = dialog.anchor || defaultAnchor;

            // Calculate transform based on anchor position
            const getStackTransform = (): string | undefined => {
              if (isTop) return undefined;
              switch (anchor) {
                case "right":
                  return `translateX(-${stackOffset}px)`;
                case "left":
                  return `translateX(${stackOffset}px)`;
                case "top":
                  return `translateY(${stackOffset}px)`;
                case "bottom":
                  return `translateY(-${stackOffset}px)`;
                default:
                  return undefined;
              }
            };

            return (
              <Drawer
                key={dialog.id}
                open={!isClosing}
                onClose={dialog.disableExternalClose ? undefined : closeDialog}
                anchor={anchor}
                hideBackdrop={!isTop}
                ModalProps={{
                  keepMounted: false,
                }}
                PaperProps={{
                  "aria-hidden": !isTop,
                  sx: {
                    width:
                      typeof dialog.width === "number"
                        ? `${dialog.width}${dialog.widthUnit || defaultWidthUnit}`
                        : dialog.width || defaultWidth,
                    transform: getStackTransform(),
                    transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
                    boxShadow: isTop
                      ? "0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)"
                      : "0px 4px 5px -2px rgba(0,0,0,0.1), 0px 7px 10px 1px rgba(0,0,0,0.07)",
                  },
                }}
                slotProps={{
                  backdrop: {
                    sx: {
                      backgroundColor: isTop
                        ? "rgba(0, 0, 0, 0.5)"
                        : "transparent",
                    },
                  },
                }}
              >
                {DialogComponent && (
                  <DialogComponent data={dialog.data} onClose={closeDialog} />
                )}
              </Drawer>
            );
          })}
        </>,
        document.body,
      )}
    </DialogServiceContext.Provider>
  );
};

/**
 * Hook to access the Dialog Service
 *
 * Supports stacked drawers - each openDialog() call pushes a new drawer onto the stack.
 * Drawers are visually layered with offsets to indicate depth.
 *
 * @example
 * ```tsx
 * const { openDialog, closeDialog, closeAll, stackDepth } = useDialogService();
 *
 * // Open/push a drawer onto the stack
 * openDialog({
 *   type: 'benefits-class-manager',
 *   data: { rfpId: '123' },
 *   width: 600
 * });
 *
 * // Push another drawer on top
 * openDialog({
 *   type: 'rfpNotes',
 *   data: { noteId: '456' }
 * });
 *
 * // Close/pop the top drawer
 * closeDialog();
 *
 * // Close all drawers
 * closeAll();
 *
 * // Check stack depth
 * console.log(`${stackDepth} drawers open`);
 * ```
 */
const useDialogService = (): DialogServiceContextType => {
  const context = useContext(DialogServiceContext);
  if (!context) {
    throw new Error(
      "useDialogService must be used within a DialogServiceProvider",
    );
  }
  return context;
};

export { DialogServiceContext, DialogServiceProvider, useDialogService };
